import { useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TourContext from 'ui-shepherd-react/contexts/TourContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Button } from 'foundations-components/transitional/components/Button';
import { Image } from 'foundations-components/transitional/components/Image';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import { closeModal, openModal
// @ts-expect-error Untyped module appActions
} from 'ContentEditorUI/redux/actions/appActions';
import { getIframeReady } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getOpenModal } from 'ContentEditorUI/redux/selectors/modalSelectors';
import { useQueryParams } from 'ContentEditorUI/utils/routingUtils';

// @ts-expect-error Untyped module eventBus
import bus from 'ContentEditorUI/coffee/lib/eventBus';
import { TourSyncController } from 'ContentEditorUI/controllers/TourSyncController';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { setHasSeenOnboardingTour } from '../../redux/actions/onboardingActions';
import { getShouldSeeOnboardingTour } from '../../redux/selectors/onboardingSelectors';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DEFAULT_MODAL_TRANSITION_TIMING } from 'foundations-components/constants';

// @ts-expect-error bender-url imports are untyped
import guideImg from 'bender-url!blog-editor-ui/img/onboarding/guide.png';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const BLOG_PLG_ONBOARDING_TOUR = 'BLOG_PLG_ONBOARDING';
export const BLOG_PLG_ONBOARDING_MODAL = 'BLOG_PLG_ONBOARDING_MODAL';
const settingsTourSteps = ['metaDescription', 'author', 'featuredImage', 'template'];
const useOpenSettingsModalForCurrentTourStep = () => {
  const {
    tour
  } = useContext(TourContext);
  const activeModal = useSelector(getOpenModal);
  const dispatch = useDispatch();
  const onStepChangeOpenModal = useCallback((stepKey, tourKey) => {
    if (tourKey && tourKey !== BLOG_PLG_ONBOARDING_TOUR) {
      return;
    }
    if (!stepKey) {
      return;
    }
    const isSettingStep = settingsTourSteps.includes(stepKey);
    const isSettingsModalOpen = activeModal === MODAL_TYPES.SETTINGS_MODAL;
    if (isSettingStep && !isSettingsModalOpen) {
      dispatch(openModal(MODAL_TYPES.SETTINGS_MODAL));
    } else if (isSettingsModalOpen && !isSettingStep) {
      dispatch(closeModal(MODAL_TYPES.SETTINGS_MODAL));
    }
  }, [activeModal, dispatch]);
  useEffect(() => {
    const activeTour = tour.getTour();
    if (activeTour !== BLOG_PLG_ONBOARDING_TOUR) {
      return undefined;
    }
    const [unsubscribe] = tour.subscribe(onStepChangeOpenModal);
    return unsubscribe;
  }, [onStepChangeOpenModal, tour]);
};
const useShowBlogOnboardingTour = () => {
  const dispatch = useDispatch();
  const {
    tour
  } = useContext(TourContext);
  const iframeReady = useSelector(getIframeReady);
  const shouldSeeOnboardingTour = useSelector(getShouldSeeOnboardingTour);
  const activeTour = tour.getTour();
  const isActiveTour = activeTour === BLOG_PLG_ONBOARDING_TOUR;
  const isAnyOtherTourActive = activeTour !== null && !isActiveTour;
  useEffect(() => {
    if (!shouldSeeOnboardingTour || !iframeReady || isActiveTour || isAnyOtherTourActive) {
      return;
    }
    tour.activate(BLOG_PLG_ONBOARDING_TOUR);
    dispatch(openModal(BLOG_PLG_ONBOARDING_MODAL));
  }, [iframeReady, isActiveTour, isAnyOtherTourActive, shouldSeeOnboardingTour, tour, dispatch]);
};
export const useStartOnboardingTour = () => {
  const dispatch = useDispatch();
  const {
    tour
  } = useContext(TourContext);
  return useCallback(() => {
    dispatch(closeModal());
    if (tour.getTour() !== BLOG_PLG_ONBOARDING_TOUR) {
      tour.activate(BLOG_PLG_ONBOARDING_TOUR);
    }
    window.setTimeout(() => {
      tour.start();
    }, parseInt(DEFAULT_MODAL_TRANSITION_TIMING, 10));
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'started-onboarding-tour-from-modal'
    });
  }, [dispatch, tour]);
};
export const OnboardingModal = () => {
  const dispatch = useDispatch();
  const {
    generatedByContentIdeas = false
  } = useQueryParams();
  useEffect(() => {
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'saw-onboarding-tour-modal'
    });
  }, []);
  const startTour = useStartOnboardingTour();
  const skipTour = useCallback(() => {
    dispatch(setHasSeenOnboardingTour(true));
    dispatch(closeModal());
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'skipped-onboarding-tour-from-modal'
    });
  }, [dispatch]);
  return /*#__PURE__*/_jsxs(Modal, {
    width: 554,
    use: "conversational",
    "data-test-id": "onboarding-modal",
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(H4, {
        marginBlockEnd: "500",
        children: generatedByContentIdeas ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.modal.headerAi"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.modal.header"
        })
      })
    }), /*#__PURE__*/_jsxs(DialogBody, {
      paddingInline: generatedByContentIdeas ? '1500' : '1600',
      paddingBlockStart: "0",
      children: [/*#__PURE__*/_jsx("p", {
        children: generatedByContentIdeas ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.modal.bodyAi"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.modal.body"
        })
      }), /*#__PURE__*/_jsx(Image, {
        src: guideImg,
        responsive: false,
        paddingBlockStart: "1000",
        paddingBlockEnd: "500"
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      align: "center",
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: startTour,
        marginInlineEnd: "500",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.modal.button.start"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: skipTour,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "plgOnboardingTour.modal.button.dismiss"
        })
      })]
    })]
  });
};
export const useTourCallbacks = () => {
  const dispatch = useDispatch();
  const hasFinishedTour = useRef(false);
  const onBeforeStart = useCallback(() => {
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'started-onboarding-tour'
    });
  }, []);
  const onBeforeExit = useCallback(({
    stepKey
  }) => {
    // Prevent firing a 'skipped-tour' event after the tour is completed (finished)
    // since UITour will call both beforeFinish and beforeExit in this scenario,
    // while only beforeExit is called when a tour is skipped (exited) by closing a step.
    if (hasFinishedTour.current) {
      return;
    }
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'skipped-onboarding-tour',
      subscreen: stepKey
    });
    dispatch(setHasSeenOnboardingTour(true));
  }, [dispatch]);
  const onBeforeFinish = useCallback(() => {
    hasFinishedTour.current = true;
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'completed-onboarding-tour'
    });
    dispatch(setHasSeenOnboardingTour(true));
  }, [dispatch]);
  const onBeforeNextStep = useCallback(({
    stepKey
  }) => {
    UsageTracker.trackEditorInteraction({
      action: 'Blog Onboarding',
      event: 'completed-onboarding-tour-step',
      subscreen: stepKey
    });
  }, []);
  return {
    onBeforeStart,
    onBeforeExit,
    onBeforeFinish,
    onBeforeNextStep
  };
};
export const useTourConfig = () => {
  const {
    onBeforeStart,
    onBeforeExit,
    onBeforeFinish
  } = useTourCallbacks();
  return {
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    config: {
      beforeExit: onBeforeExit,
      beforeFinish: onBeforeFinish,
      beforeStart: onBeforeStart,
      steps: ['postName', 'postBody', 'metaDescription', 'author', 'featuredImage', 'template', 'publish']
    }
  };
};
export const OnboardingTourController = () => {
  useShowBlogOnboardingTour();
  useOpenSettingsModalForCurrentTourStep();
  return /*#__PURE__*/_jsx(TourSyncController, {
    bus: bus,
    tourKey: BLOG_PLG_ONBOARDING_TOUR,
    tourEnv: "parent"
  });
};